import {
  Collapse,
  ConfigProvider,
  Progress,
  ProgressProps,
  Row,
  Tooltip,
  Typography,
} from "antd-v5";
import message from "components/CustomMessage";
import DeletePopup from "components/DeletePopup";
import { MenuIcon } from "components/Icons";
import InternalLink from "components/InternalLink";
import { useActiveUserContext } from "contexts/ActiveUserContext";
import usePaginationControls from "hooks/usePaginationControls";
import { TableCustomizationHelper } from "hooks/useTableCustomizationHelper";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import FindingAPI from "restAPI/FindingAPI";
import { CorrelationsResponse } from "types/api/correlationsByFindingId";
import { FindingSummary } from "types/api/findingSummary";
import { FilterOptionInfo } from "types/pagination";
import ModalUtil from "utils/modalUtil";
import { CellRenderers, formatNumberWithCommas } from "utils/tableUtils";
import { getUrlSearch } from "utils/urlUtil";
import { correlationNameRenderer } from "views/Findings/FindingList/FindingListGroupByTable";
import { getSortParamForAPI } from "views/Findings/FindingList/FindingListProvider";

import ACButtonGroup, { ACButtonGroupItem } from "ac-components/ACButtonGroup";
import ACDropdown from "ac-components/ACDropdown";
import ACPaginationBar from "ac-components/ACPaginationBar/ACPaginationBar";
import ACTable from "ac-components/ACTable/ACTable";
import ACAIBadge from "ac-components/ac-ai-badge/ACAIBadge";
import ACButton from "ac-components/ac-button/ACButton";
import GraphIcon from "ac-components/ac-icons/GraphIcon";
import TrashIcon from "ac-components/ac-icons/TrashIcon";
import ViewFindingIcon from "ac-components/ac-icons/ViewInFindingIcon";
import ACResizableColumnCell from "ac-components/ac-resizable-column-cell/ACResizableColumnCell";

import { useFindingDetails } from "../FindingDetailsProvider";

const CONFIDENCE_COLORS: ProgressProps["strokeColor"] = {
  red: "var(--Data-Visualization-Bad-Good-Gradient-Worst, rgba(240, 68, 55, 1)) !important",
  orange:
    "var(--Data-Visualization-Bad-Good-Gradient-Bad, rgba(247, 144, 9, 1)) !important",
  green:
    "var(--Data-Visualization-Bad-Good-Gradient-Best, rgba(18, 182, 105, 1)) !important",
};

type Props = {
  correlation: CorrelationsResponse;
  tableCustomizationHelper: TableCustomizationHelper<FindingSummary>;
  refresh: () => void;
  searchText?: string;
};

const CorrelationTable = ({
  correlation,
  tableCustomizationHelper,
  refresh,
  searchText,
}: Props) => {
  const { id } = useFindingDetails();
  const { tenantId, businessUnitId } = useActiveUserContext();
  const paginationControls = usePaginationControls({ defaultPageSize: 5 });
  const { page, pageSize, onPageChange, onSortChange, sortInfo } =
    paginationControls;

  const [dataSource, setDataSource] = useState([]);
  const [totalElements, setTotalElements] = useState(0);

  const { columns: customizedColumns } = tableCustomizationHelper;

  useEffect(() => {
    const sortColumns = getSortParamForAPI(sortInfo);
    const filters: FilterOptionInfo = {};
    if (searchText && !_.isNaN(+searchText)) {
      filters.id_term = [searchText];
    }

    const { promise, cancel } = FindingAPI.getAll({
      filters: {
        correlationIds: [correlation.id],
        ...filters,
      },
      sortColumns,
      page: page - 1,
      size: pageSize,
      ticketStatusRequired: false,
      ignoreMitigated: null,
      ignoreDuplicate: true,
      includeColumns: [
        "id",
        "dfId",
        "testType",
        "scanType",
        "dfTitle",
        "dfFilePath",
        "action",
        "tenant",
        "businessUnitId",
      ],
    });

    promise.then((res) => {
      setDataSource(res.content);
      setTotalElements(res.totalElements);
    }, console.error);

    return cancel;
  }, [correlation.id, page, pageSize, sortInfo, searchText]);

  const onDelete = useCallback(
    (record: FindingSummary) => {
      ModalUtil.show({
        content: (
          <DeletePopup
            // @ts-expect-error - Property 'promptMessage' does not exist on type 'IntrinsicAttributes & RefAttributes<any>'.ts(2322)
            promptMessage={`You want to delete this correlated finding: ${record.id} ?`}
            onDelete={async () => {
              await FindingAPI.deleteCorrelatedV2(id, record.id, correlation.id)
                .promise.then(() => {
                  message.success("Correlated finding Deleted Successfully");
                  refresh();
                })
                .catch((error) => {
                  console.error(error);
                });
            }}
          />
        ),
      });
    },
    [id, correlation.id, refresh],
  );

  const actionRenderer = useCallback(
    (record: FindingSummary) => {
      return (
        <ACDropdown
          items={[
            {
              label: "Delete",
              value: "delete",
              disabled: record.id === id,
              icon: <TrashIcon />,
            },
          ]}
          onOptionClick={() => onDelete(record)}
          placement="bottomRight"
        >
          <ACButton size="sm" iconOnly type="ghost">
            <MenuIcon />
          </ACButton>
        </ACDropdown>
      );
    },
    [id, onDelete],
  );

  const searchParams = useMemo(
    () =>
      getUrlSearch({
        pagination: {
          page: 0,
        },
        groupBy: ["correlationName", "remediationAction"],
        filters: { correlationIds: [correlation.id] },
      }),
    [correlation.id],
  );

  const pannelBody = (
    <>
      <Row className="collapse-panel-body">
        <Tooltip title="Confidence">
          {_.isNil(correlation.confidence) ? (
            <Progress
              strokeWidth={15}
              size={26}
              type="circle"
              percent={0}
              format={() => CellRenderers.VALUE_OR_NA()}
            />
          ) : (
            <Progress
              strokeWidth={15}
              strokeColor={
                CONFIDENCE_COLORS[
                  correlation.confidence <= 25
                    ? "red"
                    : correlation.confidence <= 75
                      ? "orange"
                      : "green"
                ]
              }
              size={26}
              type="circle"
              percent={correlation.confidence}
              format={(percent) => percent}
            />
          )}
        </Tooltip>
        <ACButtonGroup size="md">
          <ACButtonGroupItem>
            <a
              href={`/#/findings/${tenantId}/${businessUnitId}?${searchParams}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ViewFindingIcon />
            </a>
          </ACButtonGroupItem>
          <InternalLink
            to={`/findings/${id}/correlation/${correlation.id}`}
            target="_blank"
          >
            <ACButtonGroupItem>
              <GraphIcon /> View Graph
            </ACButtonGroupItem>
          </InternalLink>
        </ACButtonGroup>
      </Row>
      <ACTable<FindingSummary>
        autoEnableScroll
        computeTableWidth
        columns={customizedColumns}
        dataSource={dataSource}
        sortInfo={sortInfo}
        onSortChange={onSortChange}
        pagination={{ pageSize }}
        rowKey="id"
        customizationHelpers={tableCustomizationHelper}
        actionRender={actionRenderer}
        actionColumnWidth={65}
        components={{
          header: {
            cell: ACResizableColumnCell,
          },
        }}
      />
      <ACPaginationBar
        size="md"
        total={totalElements}
        page={page}
        onChange={onPageChange}
        pageSize={pageSize}
        showSizeChanger={false}
        stickyBottom
        message={
          <>
            <Typography.Text type="secondary">
              {formatNumberWithCommas(totalElements)} Finding
              {totalElements === 1 ? "" : "s"}
            </Typography.Text>
          </>
        }
      />
    </>
  );

  const pannelHeader = (
    <>
      <div style={{ fontWeight: 600 }}>
        {correlationNameRenderer(correlation.correlationName)}
        {correlation.source === "AI" && (
          <ACAIBadge className="m-l-xs" type="correlatedFinding" />
        )}
      </div>
      <div>{correlation.remediationAction}</div>
    </>
  );

  const panelStyle: React.CSSProperties = {
    padding: 0,
    margin: 0,
    borderRadius: 6,
    border: "none",
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Collapse: {
            contentPadding: 0,
            headerPadding: "10px 15px 0 15px",
            headerBg: "white",
          },
          Progress: {
            circleTextFontSize: "8px",
            circleTextColor: "black !important",
          },
        },
      }}
    >
      <Collapse
        style={{
          width: "100%",
        }}
        bordered={false}
        defaultActiveKey={["1"]}
        expandIconPosition="end"
        items={[
          {
            key: "1",
            label: pannelHeader,
            children: pannelBody,
            style: panelStyle,
          },
        ]}
      />
    </ConfigProvider>
  );
};

export default CorrelationTable;
